import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaceSmileIcon } from '@heroicons/react/24/outline';
import { eventsAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { getErrorMessage } from '../../utils/errorMessage';
import type { MyTrainingFeedbackState, TrainingFeedbackSummary } from '../../types/trainingFeedback';

interface TrainingFeedbackSectionProps {
  eventId: number;
  startTime: string;
  /** Stáb: összesítő; játékos/szülő: kitöltő űrlap. */
  staffView: boolean;
  /** Az értesítésből érkezve rögtön ide görgetünk. */
  autoFocus?: boolean;
}

const RATING_FACES = ['😣', '😕', '😐', '🙂', '😄'];

/** RPE skála rövid magyarázattal, hogy a gyerekek is értsék. */
const rpeHint = (rpe: number) =>
  rpe <= 2 ? 'nagyon könnyű' : rpe <= 4 ? 'könnyű' : rpe <= 6 ? 'közepes' : rpe <= 8 ? 'nehéz' : 'nagyon nehéz';

/**
 * Edzés utáni visszajelzés: mennyire tetszett, mennyire volt nehéz, megjegyzés.
 * A játékos / szülő kitölti, az edző az átlagokat és a megjegyzéseket látja.
 */
const TrainingFeedbackSection: React.FC<TrainingFeedbackSectionProps> = ({ eventId, startTime, staffView, autoFocus = false }) => {
  const started = new Date(startTime).getTime() <= Date.now();
  return started ? (
    staffView ? <StaffSummary eventId={eventId} /> : <MyFeedback eventId={eventId} autoFocus={autoFocus} />
  ) : null;
};

const StaffSummary: React.FC<{ eventId: number }> = ({ eventId }) => {
  const [summary, setSummary] = useState<TrainingFeedbackSummary | null>(null);

  useEffect(() => {
    eventsAPI
      .getFeedbackSummary(eventId)
      .then(setSummary)
      // Nem az ő korosztálya: nincs mit mutatni
      .catch(() => setSummary(null));
  }, [eventId]);

  if (!summary || summary.responseCount === 0) return null;

  return (
    <div className="mb-6 rounded-lg border border-gray-200 p-4">
      <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900">
        <FaceSmileIcon className="h-5 w-5 text-gray-400" />
        Játékosok visszajelzése
      </h4>
      <div className="mb-3 grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg bg-gray-50 p-2">
          <div className="text-lg font-semibold text-gray-900">
            {summary.responseCount}/{summary.participantCount || '?'}
          </div>
          <div className="text-xs text-gray-500">válaszolt</div>
        </div>
        <div className="rounded-lg bg-gray-50 p-2">
          <div className="text-lg font-semibold text-gray-900">{summary.averageRating?.toFixed(1) ?? '-'} / 5</div>
          <div className="text-xs text-gray-500">tetszés</div>
        </div>
        <div className="rounded-lg bg-gray-50 p-2">
          <div className="text-lg font-semibold text-gray-900">{summary.averageRpe?.toFixed(1) ?? '-'} / 10</div>
          <div className="text-xs text-gray-500">nehézség (RPE)</div>
        </div>
      </div>
      <ul className="max-h-56 space-y-2 overflow-y-auto">
        {summary.items.map((item) => (
          <li key={item.playerId} className="rounded bg-gray-50 px-3 py-2 text-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-gray-900">{item.playerName}</span>
              <span className="shrink-0 text-gray-600">
                {RATING_FACES[item.rating - 1]} · RPE {item.rpe}
              </span>
            </div>
            {item.comment && <p className="mt-1 text-gray-700">{item.comment}</p>}
          </li>
        ))}
      </ul>
    </div>
  );
};

const MyFeedback: React.FC<{ eventId: number; autoFocus: boolean }> = ({ eventId, autoFocus }) => {
  const { showToast } = useToast();
  const [state, setState] = useState<MyTrainingFeedbackState | null>(null);
  const [drafts, setDrafts] = useState<Record<number, { rating: number; rpe: number; comment: string }>>({});
  const [savingId, setSavingId] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const data = await eventsAPI.getMyFeedback(eventId);
      setState(data);
      setDrafts(
        Object.fromEntries(
          data.players.map((p) => [
            p.playerId,
            { rating: p.feedback?.rating ?? 0, rpe: p.feedback?.rpe ?? 0, comment: p.feedback?.comment ?? '' },
          ])
        )
      );
    } catch {
      setState(null);
    }
  }, [eventId]);

  useEffect(() => {
    load();
  }, [load]);

  // Az értesítésből érkezve rögtön ide görgetünk
  useEffect(() => {
    if (autoFocus && state?.canSubmit) {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [autoFocus, state]);

  if (!state || !state.enabled || state.players.length === 0) return null;
  // Lezárt visszajelzésnél csak akkor mutatjuk, ha volt kitöltve
  if (!state.canSubmit && state.players.every((p) => !p.feedback)) return null;

  const update = (playerId: number, patch: Partial<{ rating: number; rpe: number; comment: string }>) =>
    setDrafts((prev) => ({ ...prev, [playerId]: { ...prev[playerId], ...patch } }));

  const save = async (playerId: number) => {
    const draft = drafts[playerId];
    if (!draft.rating || !draft.rpe) {
      showToast('Válaszd ki, mennyire tetszett és mennyire volt nehéz.', 'error');
      return;
    }
    setSavingId(playerId);
    try {
      await eventsAPI.saveFeedback(eventId, {
        playerId,
        rating: draft.rating,
        rpe: draft.rpe,
        comment: draft.comment.trim() || undefined,
      });
      showToast('Köszönjük a visszajelzést!');
      await load();
    } catch (err) {
      showToast(getErrorMessage(err, 'A mentés nem sikerült.'), 'error');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div ref={ref} className="mb-6 rounded-lg border border-primary-200 bg-primary-50/40 p-4">
      <h4 className="mb-1 flex items-center gap-2 text-sm font-semibold text-gray-900">
        <FaceSmileIcon className="h-5 w-5 text-primary-600" />
        Milyen volt az edzés?
      </h4>
      <p className="mb-3 text-xs text-gray-500">Az edző ebből látja, hogyan érezték magukat a játékosok.</p>

      <div className="space-y-4">
        {state.players.map((p) => {
          const draft = drafts[p.playerId] ?? { rating: 0, rpe: 0, comment: '' };
          const disabled = !state.canSubmit || savingId === p.playerId;
          return (
            <div key={p.playerId} className="space-y-3">
              {state.players.length > 1 && <p className="text-sm font-medium text-gray-900">{p.playerName}</p>}

              <div>
                <p className="mb-1 text-xs font-medium text-gray-600">Mennyire tetszett?</p>
                <div className="flex gap-1">
                  {RATING_FACES.map((face, i) => (
                    <button
                      key={i}
                      type="button"
                      disabled={disabled}
                      onClick={() => update(p.playerId, { rating: i + 1 })}
                      aria-label={`${i + 1} / 5`}
                      className={`flex h-11 w-11 items-center justify-center rounded-full text-2xl transition ${
                        draft.rating === i + 1 ? 'bg-primary-600 ring-2 ring-primary-300' : 'bg-white ring-1 ring-gray-200'
                      } disabled:opacity-60`}
                    >
                      {face}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-1 text-xs font-medium text-gray-600">
                  Mennyire volt nehéz?{draft.rpe ? ` – ${draft.rpe}: ${rpeHint(draft.rpe)}` : ''}
                </p>
                <div className="grid grid-cols-10 gap-1">
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                    <button
                      key={n}
                      type="button"
                      disabled={disabled}
                      onClick={() => update(p.playerId, { rpe: n })}
                      className={`h-9 rounded text-sm font-medium ${
                        draft.rpe === n
                          ? 'bg-primary-600 text-white'
                          : n <= 4
                            ? 'bg-green-50 text-green-800'
                            : n <= 7
                              ? 'bg-amber-50 text-amber-800'
                              : 'bg-red-50 text-red-800'
                      } disabled:opacity-60`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              <input
                type="text"
                maxLength={500}
                disabled={disabled}
                value={draft.comment}
                onChange={(e) => update(p.playerId, { comment: e.target.value })}
                placeholder="Megjegyzés (nem kötelező)"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
              />

              {state.canSubmit && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-500">{p.feedback ? 'Elküldve - módosíthatod.' : ''}</span>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => save(p.playerId)}
                    className="rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    {savingId === p.playerId ? 'Küldés...' : p.feedback ? 'Módosítás' : 'Küldés'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TrainingFeedbackSection;
